import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Loader2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { StripeService, UserSubscription } from '../services/stripeService';

const SUBSCRIPTION_UI_CHECK_TIMEOUT_MS = 12000;

async function withTimeout<T>(promiseLike: PromiseLike<T>, timeoutMs: number, operation: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      Promise.resolve(promiseLike),
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`${operation} timed out`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

interface PaymentFormStripeProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When true, show subscribe-only copy (no trial offer). Used on trial-expired gate. */
  trialExpired?: boolean;
}

export const PaymentFormStripe: React.FC<PaymentFormStripeProps> = ({ onSuccess, onCancel, trialExpired = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFreeUser, setIsFreeUser] = useState(false);
  const [isOnTrial, setIsOnTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [checkingTrial, setCheckingTrial] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState<UserSubscription | null>(null);

  // Get Stripe price ID from environment or use default
  // This should be your $5/month Stripe price ID
  const priceId = process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY || '';

  // Check if user is already on trial
  useEffect(() => {
    const checkTrialStatus = async () => {
      try {
        const { data: { user } } = await withTimeout(
          supabase.auth.getUser(),
          SUBSCRIPTION_UI_CHECK_TIMEOUT_MS,
          'Trial status check'
        );
        setIsFreeUser(user?.app_metadata?.is_free === true);
        const trialEndsAtValue = user?.user_metadata?.trial_ends_at || null;
        setTrialEndsAt(trialEndsAtValue);

        if (trialEndsAtValue) {
          const trialEndDate = new Date(trialEndsAtValue);
          setIsOnTrial(trialEndDate.getTime() > Date.now());
        } else {
          setIsOnTrial(false);
        }
      } catch (err) {
        console.error('Error checking trial status:', err);
        setIsOnTrial(false);
        setTrialEndsAt(null);
      } finally {
        setCheckingTrial(false);
      }
    };

    checkTrialStatus();
  }, []);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const stripeService = new StripeService();
        const subscription = await withTimeout(
          stripeService.getUserSubscription(),
          SUBSCRIPTION_UI_CHECK_TIMEOUT_MS,
          'Subscription status check'
        );
        if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
          setActiveSubscription(subscription);
        } else {
          setActiveSubscription(null);
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
        setActiveSubscription(null);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, []);

  const getApiUrl = () => {
    // In local development, package.json proxy forwards /api/* to the deployed Vercel API
    // In production, this is the same origin, so it works there too
    return '/api/create-checkout-session';
  };

  const handleSubscribe = async () => {
    if (!priceId) {
      setError('Subscription price not configured. Please contact support.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be signed in to subscribe');
      }

      // Create Stripe Checkout session
      const apiUrl = getApiUrl();
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          priceId,
        }),
      });

      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error(
          'API response was not JSON. Please check that the billing API is available.'
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Error starting checkout:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (checkingTrial || checkingSubscription) {
    return (
      <div className="w-full max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const formatTrialEndDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const trialEndDate = formatTrialEndDate(trialEndsAt);
  const hasActiveSubscription = Boolean(activeSubscription) || isFreeUser;

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      {!trialExpired && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Choose Your Plan</h2>
          {hasActiveSubscription ? (
            <p className="text-muted-foreground">
              {isFreeUser ? 'This account has free access enabled' : "You're subscribed to the Monthly Plan"}
            </p>
          ) : isOnTrial ? (
            <p className="text-muted-foreground">
              Continue your subscription after your free trial ends
            </p>
          ) : (
            <p className="text-muted-foreground">
              Start with a 90-day free trial, then just $5/month
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
          {error}
        </div>
      )}

      {!trialExpired && isOnTrial && trialEndDate && !hasActiveSubscription && (
        <div className="mb-4 p-4 rounded-lg bg-primary/10 border border-primary/20 text-primary">
          <p className="font-medium">You're currently on your free trial</p>
          <p className="text-sm mt-1">Your trial ends on {trialEndDate}. Add a payment method to continue your subscription after the trial.</p>
        </div>
      )}

      <div className="grid md:grid-cols-1 gap-6 max-w-md mx-auto">
        <Card className="cursor-pointer transition-all hover:shadow-md">
          <CardHeader>
            <CardTitle className="text-2xl">Monthly Plan</CardTitle>
            <CardDescription>
              {hasActiveSubscription ? (
                <>
                  <span className="text-primary font-semibold">
                    Current plan
                  </span>
                  {' '}— $5.00/month
                </>
              ) : trialExpired ? (
                '$5.00/month — full access to all features'
              ) : isOnTrial ? (
                <>
                  <span className="text-primary font-semibold">
                    Continue after trial, then{' '}
                  </span>
                  $5.00/month
                </>
              ) : (
                <>
                  <span className="text-primary font-semibold">
                    90-day free trial, then{' '}
                  </span>
                  $5.00/month
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {!trialExpired && !isOnTrial && !isFreeUser && (
                <li className="flex items-center">
                  <span className="mr-2 text-primary">✓</span>
                  90-day free trial
                </li>
              )}
              <li className="flex items-center">
                <span className="mr-2 text-primary">✓</span>
                Full access to all features
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-primary">✓</span>
                Unlimited card tracking
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-primary">✓</span>
                Advanced analytics
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-primary">✓</span>
                Custom dashboards
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-primary">✓</span>
                Tax reporting
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full"
              onClick={handleSubscribe}
              disabled={loading || !priceId || hasActiveSubscription}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : hasActiveSubscription ? (
                isFreeUser ? 'Free Access Enabled' : 'Subscribed'
              ) : isOnTrial ? (
                'Add Payment Method'
              ) : trialExpired ? (
                'Subscribe to continue'
              ) : (
                'Start Free Trial'
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {onCancel && (
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Secure payment powered by Stripe. Cancel anytime.
      </p>
    </div>
  );
};
